/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import classNames from 'classnames';
import type React from 'react';

import { type CommonProps } from '../_common/types';
import type { TagSize } from '../tag-base';

export interface SelectableTagProps {
  /** The content of the tag */
  children?: React.ReactNode;
  /** Whether the tag is disabled */
  isDisabled?: boolean;
  /** Whether the tag is selected */
  isSelected: boolean;
  /** Number displayed in a badge after the content */
  badgeCount?: number;
  /** Icon to display before the content */
  leadingVisual?: React.ReactNode;
  /** Size of the tag */
  size?: TagSize;
  /** Callback fired when the tag is clicked */
  onClick: React.MouseEventHandler<HTMLButtonElement>;
}

export const SelectableTag = ({
  children,
  className,
  htmlAttributes,
  isSelected,
  badgeCount,
  leadingVisual,
  onClick,
  ref,
  isDisabled = false,
  size = 'medium',
  style,
  ...restProps
}: CommonProps<'button', SelectableTagProps>) => {
  const classes = classNames('ndl-tag-selectable', className, {
    'ndl-disabled': isDisabled,
    'ndl-large': size === 'large',
    'ndl-medium': size === 'medium',
    'ndl-small': size === 'small',
    'ndl-tag-selected': isSelected,
    'ndl-x-small': size === 'x-small',
  });

  return (
    <button
      type="button"
      aria-pressed={isSelected}
      className={classes}
      disabled={isDisabled}
      onClick={onClick}
      ref={ref}
      style={style}
      {...htmlAttributes}
      {...restProps}
    >
      {Boolean(leadingVisual) && (
        <span className="ndl-selectable-tag-leading-visual">
          {leadingVisual}
        </span>
      )}
      <span className="ndl-selectable-tag-content">{children}</span>
      {badgeCount !== undefined && (
        <span className="ndl-selectable-tag-badge">{badgeCount}</span>
      )}
    </button>
  );
};
